import _ from 'lodash';
import React from 'react';
import styled from 'styled-components';
import { Flex, Box, Text } from 'rebass/styled-components';
import { width } from 'styled-system';
import { observer } from 'mobx-react';
import { capitalize } from 'underscore.string';
import { paymentMethodsToDisplayNames } from 'shared/constants';
import useStores from 'shared/hooks/use-stores';
import { consolidateIncentives, incentivesPerLineItem } from 'shared/order/incentive-helpers.js';
import { SkeletonLoader } from 'shared/components';
import { useObserver } from 'mobx-react-lite';

import InfoIcon from './info-icon';

function CostTable({
  openConvenienceFeeModal,
  openTaxModal,
  cannabisTaxExists,
  salesTaxExists,
  bottleDepositTaxExists,
  receiptLineItems,
  hideRoundingLineItem = false,
  hideDiscounts = false,
}) {
  const { Cart } = useStores();
  const { order, costBreakdown } = Cart;
  const { dispensary, paymentMethod } = order;
  const rewardsDisplayName = Cart.rewardsProgramDisplayName;
  const isCalculating = useObserver(() => Cart.isCalculating);

  const convenienceFee = 'Convenience Fee';
  const costBreakDownDisplayMap = {
    bottleDepositTaxCents: 'Bottle Deposit Tax',
    cannabisTax: 'Cannabis Tax',
    deliveryFee: 'Delivery Fee',
    mixAndMatch: 'Mix and Match',
    otherFees: 'Other Fees',
    paymentFee: 'Payment Fee',
    productSubtotal: 'Subtotal',
    reward: rewardsDisplayName,
    salesTax: 'Sales Tax',
    subtotal: 'Subtotal',
    tip: 'Tip',
    tax: 'Sales Tax',
    taxableSubtotal: 'Taxable Subtotal',
    totalTax: 'Taxes',
  };

  const taxMod = (val) => (val === 0 || val === false ? `Included` : `$${val.toFixed(2)}`);
  const paymentMod = (val) => `$${val.toFixed(2)}`;
  const costModifiers = {
    otherFees: (val) => `$${val.toFixed(2)}`,
    paymentFee: paymentMod,
    tax: taxMod,
    cannabisTax: taxMod,
    mixAndMatch: (val) => `- $${val.toFixed(2)}`,
    discount: (val) => `- $${val.toFixed(2)}`,
    credit: (val) => `- $${val.toFixed(2)}`,
    default: (val) => `$${val.toFixed(2)}`,
    reward: (val) => `- $${val.toFixed(2)}`,
    rounding: (val) => (_.inRange(val, -100, 0) ? `- $${Math.abs(val).toFixed(2)}` : `$${val.toFixed(2)}`),
  };
  const orderDiscountPreTax = [
    `productSubtotal`,
    `mixAndMatch`,
    `discount`,
    `reward`,
    `totalTax`,
    `deliveryFee`,
    `paymentFee`,
    `otherFees`,
  ];
  const orderDiscountPostTax = [
    `productSubtotal`,
    `mixAndMatch`,
    `totalTax`,
    `reward`,
    `discount`,
    `deliveryFee`,
    `paymentFee`,
    `otherFees`,
  ];
  const applyDiscountPreTax = _.includes([`both`, `discountsFirst`], dispensary?.taxConfig?.discountTaxOrder);
  const billItems = applyDiscountPreTax ? orderDiscountPreTax : orderDiscountPostTax;
  if (!hideRoundingLineItem) {
    billItems.push('rounding');
  }

  const paymentIncentivesRewarded = costBreakdown.paymentIncentives;
  let costs = _.sortBy(
    _.omitBy(receiptLineItems, (lineItem) => !_.includes(billItems, lineItem.type) || parseFloat(lineItem.value) === 0),
    (elem) => billItems.indexOf(elem.type)
  );

  let consolidatedIncentives = [];
  let totalIncentives = 0;
  if (paymentIncentivesRewarded?.length) {
    const lineItemIncentives = incentivesPerLineItem(paymentIncentivesRewarded) ?? [];
    consolidatedIncentives = consolidateIncentives(lineItemIncentives);
    totalIncentives = consolidatedIncentives.length ? _.sumBy(consolidatedIncentives, 'value') : 0;
    costs = _.sortBy(
      _.omitBy(
        receiptLineItems,
        (lineItem) =>
          !_.includes(billItems, lineItem.type) ||
          parseFloat(lineItem.type === 'discount' ? lineItem.value - totalIncentives : lineItem.value) <= 0
      ),
      (elem) => billItems.indexOf(elem.type)
    );

    // We want to incentives to be displayed after the subtotal and before any other line items
    const incentiveIndex = costs.findIndex((lineItem) => lineItem.type === 'productSubtotal');
    costs.splice(incentiveIndex + 1, 0, ...consolidatedIncentives);
  }

  const displayTaxes = salesTaxExists || cannabisTaxExists || bottleDepositTaxExists;
  return (
    <>
      {costs &&
        _.map(costs, (lineItem) => {
          let name = _.get(costBreakDownDisplayMap, lineItem.type, capitalize(lineItem.type));
          if (lineItem.type === 'paymentFee') {
            name = name.replace(`Payment`, _.get(paymentMethodsToDisplayNames, paymentMethod, paymentMethod));
          }

          const isIncentiveItem = consolidatedIncentives.includes(lineItem);

          return (
            <Flex pb='16px' key={lineItem.type}>
              <CostItem
                dontCapitalize={lineItem.type === 'mixAndMatch'}
                width={isIncentiveItem ? 2 / 3 : 1 / 2}
                isIncentive={isIncentiveItem}
              >
                {name}:{' '}
                {lineItem.type === 'totalTax' && displayTaxes && (
                  <InfoIconContainer>
                    <StyledClickable aria-label='Tax information' onClick={openTaxModal}>
                      <InfoIcon />
                    </StyledClickable>
                  </InfoIconContainer>
                )}
              </CostItem>
              {isCalculating ? (
                <SkeletonLoader height='25' />
              ) : (
                <CostItem width={1 / 2} textAlign='right' fontWeight='bold' isIncentive={isIncentiveItem}>
                  {_.get(
                    costModifiers,
                    lineItem.type,
                    isIncentiveItem ? costModifiers.discount : costModifiers.default
                  )(lineItem.value)}
                </CostItem>
              )}
            </Flex>
          );
        })}
    </>
  );
}

export default observer(CostTable);

const InfoIconContainer = styled(Box)`
  display: inline;
  position: relative;
  cursor: pointer;
`;

const StyledClickable = styled.button`
  appearance: none;
  position: absolute;
  left: 3px;
  top: 1px;
  border: none;
  padding: 0;
  background-color: transparent;
  cursor: pointer;
`;

const CostItem = styled(Text)`
  ${width};
  text-transform: ${({ dontCapitalize }) => (dontCapitalize ? 'none' : 'capitalize')};
  font-size: 13px;
  color: ${({ isIncentive }) => (isIncentive ? '#00A47C' : '#606369')};
`;
